import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  AlertTriangle,
  Ban,
  BarChart3,
  Check,
  CheckCircle2,
  CircleDollarSign,
  Download,
  Gauge,
  Info,
  Loader2,
  LockKeyhole,
  RefreshCcw,
  Save,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Wifi,
  WifiOff,
  X,
  Zap,
} from "lucide-react";
import {
  emergencyStopRiskGuard,
  getRiskGuardAgentTokens,
  getRiskGuardConfig,
  getRiskGuardEvents,
  getRiskGuardStatus,
  getStoredAgentToken,
  resumeRiskGuard,
  saveRiskGuardConfig,
} from "../api/api";

const STORAGE_AGENT = "fm:riskguard-agent-token";
const AGENT_UPDATE_NOTICE_KEY = "fm:riskguard-agent-update-notice:v1";
const AGENT_PAGE_PATH = "/agent";
const POLL_INTERVAL_MS = 2500;

const DEFAULT_CONFIG = {
  enabled: true,
  max_daily_loss_pct: 3,
  max_drawdown_pct: 8,
  max_risk_per_trade_pct: 1,
  max_open_positions: 5,
  max_total_exposure_pct: 25,
  min_margin_level_pct: 150,
  max_consecutive_losses: 3,
  cooldown_minutes: 30,
  lock_on_violation: true,
  require_stop_loss: false,
  block_new_orders: false,
};

const PRESETS = {
  prudent: {
    max_daily_loss_pct: 2,
    max_drawdown_pct: 5,
    max_risk_per_trade_pct: 0.5,
    max_open_positions: 3,
    max_total_exposure_pct: 15,
    min_margin_level_pct: 250,
    max_consecutive_losses: 2,
    cooldown_minutes: 45,
    lock_on_violation: true,
    require_stop_loss: false,
  },
  balanced: {
    max_daily_loss_pct: 3,
    max_drawdown_pct: 8,
    max_risk_per_trade_pct: 1,
    max_open_positions: 5,
    max_total_exposure_pct: 25,
    min_margin_level_pct: 150,
    max_consecutive_losses: 3,
    cooldown_minutes: 30,
    lock_on_violation: true,
    require_stop_loss: false,
  },
  flexible: {
    max_daily_loss_pct: 5,
    max_drawdown_pct: 12,
    max_risk_per_trade_pct: 2,
    max_open_positions: 8,
    max_total_exposure_pct: 40,
    min_margin_level_pct: 120,
    max_consecutive_losses: 5,
    cooldown_minutes: 15,
    lock_on_violation: true,
    require_stop_loss: false,
  },
};

const COPY = {
  fr: {
    title: "Risk Guard",
    eyebrow: "FULLMETRIX · GESTION DU RISQUE",
    restore: "Annuler les changements",
    activeProfile: "Profil actif",
    customization: "Personnalisé",
    riskEstimate: "Score calculé uniquement à partir des mesures du compte et de leur proximité avec vos limites. Un arrêt manuel n'augmente pas artificiellement ce score.",
    riskDisabledHint: "Activez Risk Guard dans l’Agent pour afficher un score de risque.",
    loadingData: "Chargement des informations de protection…",
    dataNotReady: "Les données de suivi ne sont pas encore disponibles.",
    subtitle:
      "Protégez votre compte MT5 avec des limites automatiques vérifiées par l'Agent IA avant chaque nouvelle exécution.",
    refresh: "Actualiser",
    save: "Enregistrer",
    saved: "Enregistré",
    unsaved: "Modifications non enregistrées",
    upToDate: "Configuration à jour",
    loading: "Chargement de Risk Guard…",
    loadAgentsError: "Impossible de récupérer l'Agent IA.",
    loadError: "Impossible de charger les données Risk Guard.",
    saveError: "Impossible d'enregistrer la configuration.",
    methodError:
      "La route Risk Guard du serveur n'accepte pas encore cette action.",
    stopError: "Impossible d'activer l'arrêt d'urgence.",
    resumeError: "Impossible de réautoriser le trading.",
    invalidConfig: "Certaines limites sont invalides. Vérifiez les valeurs avant d'enregistrer.",
    noAgentTitle: "Aucun Agent IA disponible",
    noAgentText:
      "Connectez d'abord votre Agent IA MT5. Risk Guard utilisera automatiquement ce compte, sans sélection supplémentaire.",

    configured: "Configuré",
    guardStatus: "Risk Guard",
    mt5Account: "Compte MT5",
    currentRisk: "Risque actuel",
    lastSync: "Dernière synchro",
    protected: "Protection active",
    blocked: "Trading bloqué",
    disabled: "Protection désactivée",
    online: "Agent connecté",
    offline: "Agent hors ligne",
    neverSynced: "Jamais synchronisé",
    waitingConnection: "En attente de connexion",
    waitingData: "En attente des données MT5",
    riskControlled: "Risque sous contrôle",
    riskWatch: "À surveiller",
    riskCritical: "Limite atteinte",
    manualStopActive: "Arrêt d’urgence actif",
    safetyPause: "Pause de sécurité",
    automaticBlock: "Protection déclenchée",
    blockReasonTitle: "Pourquoi les nouvelles entrées sont bloquées",
    blockIndependentScore: "Le blocage et le score sont séparés : le score mesure le risque du compte, tandis que le blocage indique si l’Agent autorise actuellement une nouvelle entrée.",
    blockedUntil: "Blocage jusqu’à",
    marginLevel: "Niveau de marge",
    freeMargin: "Marge libre",
    marginUsed: "Marge utilisée",
    marginNotUsed: "Aucune marge utilisée",
    minLabel: "minimum",

    configuration: "Règles de protection",
    configurationDesc: "Réglez les limites que l'Agent doit contrôler avant d'autoriser un ordre.",
    quickProfiles: "Profils rapides",
    quickProfilesDesc: "Point de départ uniquement : vous pouvez modifier chaque limite ensuite.",
    prudent: "Prudent",
    balanced: "Équilibré",
    flexible: "Flexible",
    mainLimits: "Limites de trading",
    accountSafety: "Protection du compte",
    automation: "Automatisation",
    syncHint: "Après enregistrement, l'Agent récupère les nouvelles règles au prochain cycle.",

    dailyLossMax: "Perte journalière max",
    dailyLossHelp: "Bloque les nouveaux ordres à cette perte.",
    drawdownMax: "Drawdown maximal",
    drawdownHelp: "Stoppe les nouvelles entrées à ce drawdown.",
    riskPerTrade: "Risque max / trade",
    riskPerTradeHelp: "Risque accepté avant l'envoi d'un ordre.",
    simultaneousPositions: "Positions simultanées",
    simultaneousPositionsHelp: "Nombre maximal de positions ouvertes.",
    totalExposure: "Exposition totale max",
    totalExposureHelp: "Exposition cumulée maximale du compte.",
    minMargin: "Marge minimale",
    minMarginHelp: "Niveau de marge minimum autorisé.",
    consecutiveLosses: "Pertes consécutives max",
    consecutiveLossesHelp: "Bloque après cette série de pertes.",
    cooldown: "Pause après blocage",
    cooldownHelp: "Durée avant reprise après une violation.",
    positionsUnit: "positions",
    tradesUnit: "trades",
    minutesUnit: "min",

    protectionEnabled: "Risk Guard actif",
    protectionEnabledDesc: "Contrôle les règles avant chaque nouvelle exécution.",
    lockViolation: "Verrouillage après violation",
    lockViolationDesc: "Après une règle refusée, bloque aussi les nouvelles entrées pendant la durée de pause configurée.",
    requireStopLoss: "Stop Loss obligatoire",
    requireStopLossDesc: "Refuse toute nouvelle position sans Stop Loss. Désactivez cette option si vous autorisez les entrées sans SL.",
    allEntriesProtected: "Risk Guard surveille aussi les nouvelles positions ouvertes directement dans MT5 ou par un autre EA.",
    localTime: "Heure locale",
    timezoneAuto: "Fuseau détecté automatiquement",

    liveProtection: "Protection en direct",
    liveProtectionDesc: "État du compte comparé aux limites enregistrées.",
    balance: "Balance",
    equity: "Equity",
    dailyPnl: "P&L du jour",
    dailyLoss: "Perte du jour",
    drawdown: "Drawdown",
    exposure: "Exposition",
    margin: "Marge",
    positions: "Positions",
    consecutive: "Pertes consécutives",
    riskScore: "Score de risque",

    emergencyStop: "Arrêt d'urgence",
    emergencyDesc: "Bloque immédiatement toute nouvelle exécution sans fermer les positions ouvertes.",
    allowResume: "Autoriser la reprise",
    blockOrders: "Bloquer les nouveaux ordres",
    confirmStop:
      "Activer l'arrêt d'urgence ? Les nouveaux ordres seront bloqués jusqu'à la reprise manuelle.",

    goodToKnow: "À retenir",
    goodToKnowText:
      "Risk Guard ne ferme pas les positions déjà présentes. En revanche, une nouvelle position ouverte directement dans MT5 ou par un autre EA peut être fermée immédiatement si elle viole une règle active.",

    events: "Journal de protection",
    eventsDesc: "Derniers blocages, limites atteintes et reprises.",
    noEvents: "Aucun événement Risk Guard pour le moment.",

    ruleEmergency: "Arrêt d'urgence activé",
    ruleCooldown: "Protection temporairement verrouillée",
    ruleDailyLoss: "Limite de perte journalière atteinte",
    ruleDrawdown: "Limite de drawdown atteinte",
    ruleOpenPositions: "Nombre maximal de positions atteint",
    ruleExposure: "Exposition maximale atteinte",
    ruleConsecutiveLosses: "Limite de pertes consécutives atteinte",
    ruleMargin: "Niveau de marge minimum atteint",
    ruleRiskTrade: "Risque par trade supérieur à la limite",
    ruleStopLoss: "Stop Loss obligatoire : position sans SL refusée",
    ruleResume: "Trading réautorisé manuellement",
    ruleGuard: "Règle de protection déclenchée",

    agentUpdateBadge: "MISE À JOUR REQUISE",
    agentUpdateTitle: "Installez la nouvelle version de l’Agent IA",
    agentUpdateText:
      "Risk Guard dépend des contrôles ajoutés dans la dernière version de l’Agent MT5. Téléchargez et installez cette version avant d’utiliser la protection.",
    agentUpdateAction: "Aller à la page Agent",
    agentUpdateLater: "Plus tard",
  },
  en: {
    title: "Risk Guard",
    eyebrow: "FULLMETRIX · RISK MANAGEMENT",
    restore: "Discard changes",
    activeProfile: "Active profile",
    customization: "Custom",
    riskEstimate: "Score calculated only from live account metrics and their proximity to your limits. A manual stop does not artificially increase this score.",
    riskDisabledHint: "Enable Risk Guard in the Agent to display a risk score.",
    loadingData: "Loading protection information…",
    dataNotReady: "Live data is not available yet.",
    subtitle:
      "Protect your MT5 account with automatic limits checked by the AI Agent before every new execution.",
    refresh: "Refresh",
    save: "Save",
    saved: "Saved",
    unsaved: "Unsaved changes",
    upToDate: "Configuration up to date",
    loading: "Loading Risk Guard…",
    loadAgentsError: "Unable to retrieve the AI Agent.",
    loadError: "Unable to load Risk Guard data.",
    saveError: "Unable to save the configuration.",
    methodError:
      "The Risk Guard server route does not accept this action yet. Update the backend and try again.",
    stopError: "Unable to activate the emergency stop.",
    resumeError: "Unable to resume trading.",
    invalidConfig: "Some limits are invalid. Check the values before saving.",
    noAgentTitle: "No AI Agent available",
    noAgentText:
      "Connect your MT5 AI Agent first. Risk Guard will automatically use that account without another selector.",

    configured: "Configured",
    guardStatus: "Risk Guard",
    mt5Account: "MT5 account",
    currentRisk: "Current risk",
    lastSync: "Last sync",
    protected: "Protection active",
    blocked: "Trading blocked",
    disabled: "Protection disabled",
    online: "Agent connected",
    offline: "Agent offline",
    neverSynced: "Never synchronized",
    waitingConnection: "Waiting for connection",
    waitingData: "Waiting for MT5 data",
    riskControlled: "Risk under control",
    riskWatch: "Needs attention",
    riskCritical: "Limit reached",
    manualStopActive: "Emergency stop active",
    safetyPause: "Safety cooldown",
    automaticBlock: "Protection triggered",
    blockReasonTitle: "Why new entries are blocked",
    blockIndependentScore: "Blocking and risk score are separate: the score measures account risk, while blocking indicates whether the Agent currently allows a new entry.",
    blockedUntil: "Blocked until",
    marginLevel: "Margin level",
    freeMargin: "Free margin",
    marginUsed: "Used margin",
    marginNotUsed: "No margin in use",
    minLabel: "minimum",

    configuration: "Protection rules",
    configurationDesc: "Set the limits the Agent must check before authorizing an order.",
    quickProfiles: "Quick profiles",
    quickProfilesDesc: "Starting point only: every limit can still be adjusted.",
    prudent: "Conservative",
    balanced: "Balanced",
    flexible: "Flexible",
    mainLimits: "Trading limits",
    accountSafety: "Account protection",
    automation: "Automation",
    syncHint: "After saving, the Agent retrieves the new rules on its next cycle.",

    dailyLossMax: "Max daily loss",
    dailyLossHelp: "Blocks new orders at this loss level.",
    drawdownMax: "Maximum drawdown",
    drawdownHelp: "Stops new entries at this drawdown.",
    riskPerTrade: "Max risk / trade",
    riskPerTradeHelp: "Accepted risk before an order is sent.",
    simultaneousPositions: "Simultaneous positions",
    simultaneousPositionsHelp: "Maximum number of open positions.",
    totalExposure: "Max total exposure",
    totalExposureHelp: "Maximum combined account exposure.",
    minMargin: "Minimum margin",
    minMarginHelp: "Minimum allowed margin level.",
    consecutiveLosses: "Max consecutive losses",
    consecutiveLossesHelp: "Blocks after this losing streak.",
    cooldown: "Cooldown after block",
    cooldownHelp: "Delay before resuming after a violation.",
    positionsUnit: "positions",
    tradesUnit: "trades",
    minutesUnit: "min",

    protectionEnabled: "Risk Guard enabled",
    protectionEnabledDesc: "Checks the rules before every new execution.",
    lockViolation: "Lock after violation",
    lockViolationDesc: "After a rejected rule, also blocks new entries for the configured cooldown period.",
    requireStopLoss: "Require Stop Loss",
    requireStopLossDesc: "Rejects every new position without a Stop Loss. Turn this off if entries without an SL are allowed.",
    allEntriesProtected: "Risk Guard also watches new positions opened directly in MT5 or by another EA.",
    localTime: "Local time",
    timezoneAuto: "Time zone detected automatically",

    liveProtection: "Live protection",
    liveProtectionDesc: "Account state compared with the saved limits.",
    balance: "Balance",
    equity: "Equity",
    dailyPnl: "Daily P&L",
    dailyLoss: "Daily loss",
    drawdown: "Drawdown",
    exposure: "Exposure",
    margin: "Margin",
    positions: "Positions",
    consecutive: "Consecutive losses",
    riskScore: "Risk score",

    emergencyStop: "Emergency stop",
    emergencyDesc: "Immediately blocks new executions without closing existing positions.",
    allowResume: "Allow trading again",
    blockOrders: "Block new orders",
    confirmStop:
      "Activate the emergency stop? New orders will remain blocked until you manually resume trading.",

    goodToKnow: "Keep in mind",
    goodToKnowText:
      "Risk Guard does not close positions that were already open. However, a new position opened directly in MT5 or by another EA can be closed immediately if it violates an active rule.",

    events: "Protection log",
    eventsDesc: "Latest blocks, reached limits and resumptions.",
    noEvents: "No Risk Guard event yet.",

    ruleEmergency: "Emergency stop activated",
    ruleCooldown: "Protection temporarily locked",
    ruleDailyLoss: "Daily loss limit reached",
    ruleDrawdown: "Drawdown limit reached",
    ruleOpenPositions: "Maximum number of positions reached",
    ruleExposure: "Maximum exposure reached",
    ruleConsecutiveLosses: "Consecutive loss limit reached",
    ruleMargin: "Minimum margin level reached",
    ruleRiskTrade: "Risk per trade above the configured limit",
    ruleStopLoss: "Stop Loss required: position without SL rejected",
    ruleResume: "Trading manually resumed",
    ruleGuard: "Protection rule triggered",

    agentUpdateBadge: "UPDATE REQUIRED",
    agentUpdateTitle: "Install the new AI Agent version",
    agentUpdateText:
      "Risk Guard relies on controls included in the latest MT5 Agent version. Download and install that version before using the protection.",
    agentUpdateAction: "Go to Agent page",
    agentUpdateLater: "Later",
  },
};

function cx(...items) {
  return items.filter(Boolean).join(" ");
}

function cleanUiError(error, fallback, methodError) {
  const status = Number(error?.status || 0);
  const raw = String(error?.message || "").trim();

  if (status === 405 || /method\s+not\s+allowed/i.test(raw)) return methodError || fallback;
  if (!raw || /<!doctype\s+html|<html|<head|<body|<h1/i.test(raw)) return fallback;
  return raw;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function extractPayload(data, key) {
  if (!data || typeof data !== "object") return null;
  if (data[key] && typeof data[key] === "object") return data[key];
  if (data.data?.[key] && typeof data.data[key] === "object") return data.data[key];
  return data;
}

function normalizeTokens(data) {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.tokens)
      ? data.tokens
      : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.agent_tokens)
          ? data.agent_tokens
          : [];

  return list
    .map((item) => ({
      token: String(item?.token || item?.agent_token || "").trim(),
      active: item?.active !== false,
    }))
    .filter((item) => item.token && item.active);
}

function normalizeConfig(data) {
  const raw = extractPayload(data, "config") || {};
  return {
    ...DEFAULT_CONFIG,
    ...raw,
    enabled: raw.enabled === undefined ? DEFAULT_CONFIG.enabled : Boolean(raw.enabled),
    lock_on_violation:
      raw.lock_on_violation === undefined
        ? DEFAULT_CONFIG.lock_on_violation
        : Boolean(raw.lock_on_violation),
    require_stop_loss:
      raw.require_stop_loss === undefined
        ? DEFAULT_CONFIG.require_stop_loss
        : Boolean(raw.require_stop_loss),
    block_new_orders:
      raw.block_new_orders === undefined
        ? DEFAULT_CONFIG.block_new_orders
        : Boolean(raw.block_new_orders),
  };
}

function normalizeStatus(data) {
  const raw = extractPayload(data, "status") || {};
  const account = raw.account || data?.account || {};

  return {
    ...raw,
    online: Boolean(raw.online ?? raw.connected ?? raw.is_connected),
    blocked: Boolean(raw.blocked ?? raw.block_new_orders ?? raw.trading_blocked),
    effective_enabled:
      raw.effective_enabled === undefined
        ? undefined
        : Boolean(raw.effective_enabled),
    agent_enabled:
      raw.agent_enabled === undefined
        ? undefined
        : Boolean(raw.agent_enabled),
    blocked_rule: raw.blocked_rule || raw.rule_key || raw.violation_rule || "",
    blocked_reason: raw.blocked_reason || raw.reason || "",
    blocked_until: raw.blocked_until_ms ?? raw.blocked_until ?? null,
    balance: toNumber(raw.balance ?? account.balance, 0),
    equity: toNumber(raw.equity ?? account.equity ?? raw.balance ?? account.balance, 0),
    daily_pnl: toNumber(raw.daily_pnl ?? raw.pnl_today ?? raw.day_pnl, 0),
    daily_loss_pct: Math.max(0, toNumber(raw.daily_loss_pct ?? raw.day_loss_pct, 0)),
    drawdown_pct: Math.max(0, toNumber(raw.drawdown_pct ?? raw.dd_pct, 0)),
    margin_level_pct: Math.max(0, toNumber(raw.margin_level_pct ?? raw.margin_level, 0)),
    margin_used: Math.max(0, toNumber(raw.margin_used ?? raw.margin, 0)),
    open_positions: Math.max(0, Math.trunc(toNumber(raw.open_positions ?? raw.positions_count, 0))),
    total_exposure_pct: Math.max(0, toNumber(raw.total_exposure_pct ?? raw.exposure_pct, 0)),
    consecutive_losses: Math.max(0, Math.trunc(toNumber(raw.consecutive_losses, 0))),
    currency: String(raw.currency || account.currency || "USD").toUpperCase(),
    account_login: raw.account_login || raw.login || account.login || account.account_login || "",
    server: raw.server || raw.broker || account.server || account.broker || "",
    last_sync: raw.last_sync_ms ?? raw.last_sync ?? raw.last_heartbeat ?? raw.updated_at ?? null,
  };
}

function normalizeEvents(data) {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.events)
      ? data.events
      : Array.isArray(data?.items)
        ? data.items
        : [];

  return list.map((event, index) => ({
    id: event?.id ?? `${event?.created_at || "event"}-${index}`,
    rule_key: event?.rule_key || event?.rule || event?.type || "guard",
    severity: event?.severity || "info",
    created_at: event?.created_at_ms ?? event?.created_at ?? event?.timestamp ?? event?.occurred_at ?? null,
  }));
}

function sanitizeConfig(config) {
  return {
    enabled: Boolean(config.enabled),
    max_daily_loss_pct: clamp(toNumber(config.max_daily_loss_pct, 3), 0.1, 100),
    max_drawdown_pct: clamp(toNumber(config.max_drawdown_pct, 8), 0.1, 100),
    max_risk_per_trade_pct: clamp(toNumber(config.max_risk_per_trade_pct, 1), 0.01, 100),
    max_open_positions: Math.trunc(clamp(toNumber(config.max_open_positions, 5), 1, 100)),
    max_total_exposure_pct: clamp(toNumber(config.max_total_exposure_pct, 25), 0.1, 1000),
    min_margin_level_pct: clamp(toNumber(config.min_margin_level_pct, 150), 0, 10000),
    max_consecutive_losses: Math.trunc(clamp(toNumber(config.max_consecutive_losses, 3), 1, 100)),
    cooldown_minutes: Math.trunc(clamp(toNumber(config.cooldown_minutes, 30), 0, 1440)),
    lock_on_violation: Boolean(config.lock_on_violation),
    require_stop_loss: Boolean(config.require_stop_loss),
    block_new_orders: Boolean(config.block_new_orders),
  };
}

function configSignature(config) {
  return JSON.stringify(sanitizeConfig(config));
}

function isConfigValid(config) {
  const ranges = {
    max_daily_loss_pct: [0.1, 100, false],
    max_drawdown_pct: [0.1, 100, false],
    max_risk_per_trade_pct: [0.01, 100, false],
    max_open_positions: [1, 100, true],
    max_total_exposure_pct: [0.1, 1000, false],
    min_margin_level_pct: [0, 10000, false],
    max_consecutive_losses: [1, 100, true],
    cooldown_minutes: [0, 1440, true],
  };
  return Object.entries(ranges).every(([key, [min, max, integer]]) => {
    const raw = config[key];
    if (raw === "" || raw === null || raw === undefined) return false;
    const value = Number(raw);
    return Number.isFinite(value) && value >= min && value <= max && (!integer || Number.isInteger(value));
  });
}

function calculateRiskScore(status, config, { enabled, hasTelemetry }) {
  if (!enabled || !hasTelemetry || !status || !config) {
    return { available: false, score: 0, highestRatio: 0 };
  }

  const factors = [];

  const addUpperLimit = (current, limit, weight) => {
    const safeLimit = toNumber(limit, 0);
    if (safeLimit <= 0) return;
    factors.push({
      ratio: Math.max(0, toNumber(current, 0) / safeLimit),
      weight,
    });
  };

  addUpperLimit(status.daily_loss_pct, config.max_daily_loss_pct, 0.24);
  addUpperLimit(status.drawdown_pct, config.max_drawdown_pct, 0.22);
  addUpperLimit(status.total_exposure_pct, config.max_total_exposure_pct, 0.18);
  addUpperLimit(status.open_positions, config.max_open_positions, 0.14);
  addUpperLimit(status.consecutive_losses, config.max_consecutive_losses, 0.10);

  // Le niveau de marge MT5 vaut souvent 0 lorsqu'aucune marge n'est utilisée.
  // Dans ce cas il n'y a aucun risque de marge : on ne doit surtout pas traiter
  // ce 0 comme une limite critique.
  const margin = toNumber(status.margin_level_pct, 0);
  const marginUsed = toNumber(status.margin_used, 0);
  const minMargin = toNumber(config.min_margin_level_pct, 0);
  if (marginUsed > 0 && margin > 0 && minMargin > 0) {
    factors.push({
      ratio: Math.max(0, minMargin / margin),
      weight: 0.12,
    });
  }

  if (!factors.length) {
    return { available: true, score: 0, highestRatio: 0 };
  }

  const totalWeight = factors.reduce((sum, item) => sum + item.weight, 0) || 1;
  const weightedPressure =
    factors.reduce(
      (sum, item) => sum + Math.min(item.ratio, 1.5) * item.weight,
      0
    ) / totalWeight;

  const highestRatio = Math.max(...factors.map((item) => item.ratio));

  // Le score représente la pression RÉELLE des métriques, pas l'état du bouton
  // d'arrêt. Une règle proche de sa limite pèse fortement, sans forcer 100 %
  // simplement parce que le trading est bloqué manuellement.
  const rawScore =
    (weightedPressure * 0.35 + Math.min(highestRatio, 1.5) * 0.65) * 100;

  return {
    available: true,
    score: Math.round(clamp(rawScore, 0, 100)),
    highestRatio,
  };
}

function parseServerDate(value) {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  // Epoch (ms or seconds) is unambiguous and preferred when the backend sends it.
  if (/^\d{10,13}$/.test(raw)) {
    const numeric = Number(raw);
    const millis = raw.length <= 10 ? numeric * 1000 : numeric;
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // SQLAlchemy uses naive UTC datetimes internally. Older API responses were
  // emitted as "2026-10-11T12:48:35" without Z. Browsers interpret that as
  // local time, which is wrong for clients outside the server time zone.
  // Treat offset-less server timestamps as UTC, then display in the user's
  // browser time zone.
  const hasExplicitZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const looksIsoDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(raw);
  const normalized = !hasExplicitZone && looksIsoDateTime ? `${raw}Z` : raw;

  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function Card({ children, className = "" }) {
  return (
    <div
      className={cx(
        "min-w-0 rounded-[22px] border border-[#e1e6ef] bg-white shadow-[0_5px_20px_rgba(15,23,42,0.035)] dark:border-white/[0.09] dark:bg-[#19162f] dark:shadow-none",
        className
      )}
    >
      {children}
    </div>
  );
}

function SummaryItem({ icon: Icon, label, value, detail, tone = "violet" }) {
  const colors = {
    violet: "bg-violet-50 text-violet-600 dark:bg-violet-400/10 dark:text-violet-300",
    green: "bg-emerald-50 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-300",
    amber: "bg-amber-50 text-amber-600 dark:bg-amber-400/10 dark:text-amber-300",
    red: "bg-rose-50 text-rose-600 dark:bg-rose-400/10 dark:text-rose-300",
    slate: "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300",
    blue: "bg-sky-50 text-sky-600 dark:bg-sky-400/10 dark:text-sky-300",
  };
  return (
    <div className="flex min-h-[132px] min-w-0 items-start gap-4 rounded-[22px] border border-[#e1e6ef] bg-white p-5 shadow-[0_5px_18px_rgba(15,23,42,0.035)] dark:border-white/[0.09] dark:bg-[#19162f] sm:p-5.5">
      <div className={cx("grid h-12 w-12 shrink-0 place-items-center rounded-[15px]", colors[tone] || colors.violet)}>
        <Icon size={21} strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10.5px] font-semibold uppercase leading-4 tracking-[0.11em] text-slate-500 dark:text-slate-400">{label}</p>
        <p className="mt-2 break-words text-[18px] font-semibold leading-[1.3] tracking-[-0.02em] text-[#10203a] dark:text-slate-50 sm:text-[19px]">{value}</p>
        {detail ? <p className="mt-1.5 break-words text-[12px] leading-[1.45] text-slate-500 dark:text-slate-400">{detail}</p> : null}
      </div>
    </div>
  );
}

function CompactField({ label, value, onChange, suffix, min = 0, max, step = 1, help }) {
  const numericValue = value === "" ? NaN : Number(value);
  const invalid = value === "" || !Number.isFinite(numericValue) || numericValue < min || (max !== undefined && numericValue > max) || (Number.isInteger(step) && !Number.isInteger(numericValue));
  return (
    <label className={cx(
      "group flex min-w-0 flex-col rounded-[17px] border bg-white p-4 transition-colors focus-within:border-violet-400 focus-within:ring-[3px] focus-within:ring-violet-500/10 dark:bg-white/[0.025] sm:p-[18px]",
      invalid ? "border-rose-300 dark:border-rose-500/50" : "border-[#e5e9f1] hover:border-violet-200 dark:border-white/10 dark:hover:border-violet-400/30"
    )}>
      <span className="block text-[14px] font-semibold leading-5 text-[#17243b] dark:text-slate-100">{label}</span>
      <span className="mt-1.5 min-h-[34px] text-[12px] leading-[17px] text-slate-500 dark:text-slate-400">{help}</span>
      <span className="relative mt-2 block">
        <input
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          value={value ?? ""}
          aria-invalid={invalid}
          onChange={(event) => {
            const raw = event.target.value;
            onChange(raw === "" ? "" : Number(raw));
          }}
          className={cx(
            "h-12 w-full min-w-0 rounded-xl border bg-[#f9fafd] px-3.5 pr-20 text-[15.5px] font-semibold text-[#17243b] outline-none transition-colors placeholder:text-slate-400 focus:border-violet-400 focus:bg-white dark:bg-[#24203f] dark:text-white dark:focus:bg-[#2a2549]",
            invalid ? "border-rose-300 dark:border-rose-500/50" : "border-[#e5e9f1] dark:border-white/10"
          )}
        />
        {suffix ? <span className="pointer-events-none absolute inset-y-0 right-3 flex max-w-[78px] items-center justify-end text-[11px] font-normal text-slate-500 dark:text-slate-400">{suffix}</span> : null}
      </span>
    </label>
  );
}

function ToggleCard({ checked, onChange, label, description }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cx(
        "flex min-h-[91px] w-full min-w-0 items-center justify-between gap-4 rounded-[15px] border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-violet-500/20 dark:bg-white/[0.025]",
        checked ? "border-violet-200 bg-violet-50/40 hover:bg-violet-50 dark:border-violet-400/25" : "border-[#e5e9f1] bg-white hover:bg-slate-50 dark:border-white/10"
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-medium leading-5 text-[#14223b] dark:text-slate-100">{label}</span>
        <span className="mt-1 block text-[11px] leading-[17px] text-slate-500 dark:text-slate-400">{description}</span>
      </span>
      <span aria-hidden="true" className={cx("relative h-[25px] w-[46px] shrink-0 rounded-full transition-colors", checked ? "bg-violet-600" : "bg-slate-300 dark:bg-white/20")}>
        <span className={cx("absolute left-[3px] top-[3px] h-[19px] w-[19px] rounded-full bg-white shadow-sm transition-transform", checked ? "translate-x-[21px]" : "translate-x-0")} />
      </span>
    </button>
  );
}

function ProgressRow({ label, valueText, ratio, tone = "violet", detail }) {
  const width = clamp(toNumber(ratio, 0) * 100, 0, 100);
  const palette = { violet: "bg-violet-500", green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-rose-500", blue: "bg-sky-500" };
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <p className="text-[12px] font-normal leading-5 text-slate-600 dark:text-slate-300">{label}</p>
          {detail ? <p className="text-[12px] leading-[1.5] text-slate-500 dark:text-slate-400">{detail}</p> : null}
        </div>
        <span className="shrink-0 text-[12px] font-medium text-[#14223b] dark:text-white">{valueText}</span>
      </div>
      <div className="mt-2.5 h-[7px] overflow-hidden rounded-full bg-[#edf0f7] dark:bg-white/10" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(width)}>
        <div className={cx("h-full rounded-full transition-[width] duration-500", palette[tone] || palette.violet)} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function SectionHeader({ title, description }) {
  return (
    <div className="mb-3 flex flex-col gap-0.5">
      <h3 className="text-[14px] font-semibold leading-5 text-[#243650] dark:text-slate-200">{title}</h3>
      {description ? <p className="text-[12px] leading-[1.5] text-slate-500 dark:text-slate-400">{description}</p> : null}
    </div>
  );
}

export default function RiskGuardPage() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").toLowerCase().startsWith("en") ? "en" : "fr";
  const t = COPY[lang];
  const locale = lang === "en" ? "en-US" : "fr-FR";
  const userTimeZone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  const mountedRef = useRef(true);
  const refreshIdRef = useRef(0);

  const [agentToken, setAgentToken] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_AGENT) || "";
    } catch {
      return "";
    }
  });
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [baselineSignature, setBaselineSignature] = useState(configSignature(DEFAULT_CONFIG));
  const [persistedConfig, setPersistedConfig] = useState(DEFAULT_CONFIG);
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [showAgentUpdateNotice, setShowAgentUpdateNotice] = useState(() => {
    try {
      return localStorage.getItem(AGENT_UPDATE_NOTICE_KEY) !== "dismissed";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const dismissAgentUpdateNotice = useCallback(() => {
    try {
      localStorage.setItem(AGENT_UPDATE_NOTICE_KEY, "dismissed");
    } catch {}
    setShowAgentUpdateNotice(false);
  }, []);

  const goToAgentPage = useCallback(() => {
    try {
      localStorage.setItem(AGENT_UPDATE_NOTICE_KEY, "dismissed");
    } catch {}
    window.location.assign(AGENT_PAGE_PATH);
  }, []);

  const dirty = useMemo(() => configSignature(config) !== baselineSignature, [config, baselineSignature]);

  const number = useCallback(
    (value, digits = 2) =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(toNumber(value, 0)),
    [locale]
  );

  const money = useCallback(
    (value, currency = "USD") => {
      const safeCurrency = /^[A-Z]{3}$/.test(String(currency || "").toUpperCase())
        ? String(currency).toUpperCase()
        : "USD";
      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: safeCurrency,
          maximumFractionDigits: 2,
        }).format(toNumber(value, 0));
      } catch {
        return `${number(value)} ${safeCurrency}`;
      }
    },
    [locale, number]
  );

  const dateTime = useCallback(
    (value) => {
      const date = parseServerDate(value);
      if (!date) return t.neverSynced;

      try {
        return new Intl.DateTimeFormat(locale, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZone: userTimeZone,
        }).format(date);
      } catch {
        return date.toLocaleString(locale);
      }
    },
    [locale, t.neverSynced, userTimeZone]
  );

  const ruleLabel = useCallback(
    (rule) => {
      const map = {
        emergency_stop: t.ruleEmergency,
        cooldown: t.ruleCooldown,
        daily_loss: t.ruleDailyLoss,
        drawdown: t.ruleDrawdown,
        open_positions: t.ruleOpenPositions,
        exposure: t.ruleExposure,
        consecutive_losses: t.ruleConsecutiveLosses,
        margin_level: t.ruleMargin,
        risk_per_trade: t.ruleRiskTrade,
        stop_loss_required: t.ruleStopLoss,
        resume: t.ruleResume,
        guard: t.ruleGuard,
      };
      return map[String(rule || "guard")] || t.ruleGuard;
    },
    [t]
  );

  const loadAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const data = await getRiskGuardAgentTokens();
      if (!mountedRef.current) return;
      const list = normalizeTokens(data);

      const sharedToken = String(getStoredAgentToken?.() || "").trim();
      let storedToken = "";
      try {
        storedToken = String(localStorage.getItem(STORAGE_AGENT) || "").trim();
      } catch {}

      const preferred = [sharedToken, storedToken].find(
        (token) => token && list.some((agent) => agent.token === token)
      );
      const next = preferred || list[0]?.token || "";

      setAgentToken(next);
      try {
        if (next) localStorage.setItem(STORAGE_AGENT, next);
        else localStorage.removeItem(STORAGE_AGENT);
      } catch {}
      setError("");
    } catch (err) {
      if (mountedRef.current) setError(cleanUiError(err, t.loadAgentsError, t.methodError));
    } finally {
      if (mountedRef.current) setLoadingAgents(false);
    }
  }, [t.loadAgentsError, t.methodError]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const refreshLive = useCallback(
    async (silent = true) => {
      const token = String(agentToken || "").trim();
      if (!token) return;
      const refreshId = ++refreshIdRef.current;
      if (!silent) setLoading(true);

      try {
        const [statusData, eventData] = await Promise.all([
          getRiskGuardStatus(token),
          getRiskGuardEvents(token, 30),
        ]);
        if (!mountedRef.current || refreshId !== refreshIdRef.current) return;
        setStatus(normalizeStatus(statusData));
        setEvents(normalizeEvents(eventData));
        setError("");
      } catch (err) {
        if (mountedRef.current && refreshId === refreshIdRef.current) {
          setError(cleanUiError(err, t.loadError, t.methodError));
        }
      } finally {
        if (!silent && mountedRef.current && refreshId === refreshIdRef.current) setLoading(false);
      }
    },
    [agentToken, t.loadError, t.methodError]
  );

  const loadAll = useCallback(
    async (silent = false) => {
      const token = String(agentToken || "").trim();
      if (!token) {
        setLoading(false);
        return;
      }
      const refreshId = ++refreshIdRef.current;
      if (!silent) setLoading(true);

      try {
        const [statusData, configData, eventData] = await Promise.all([
          getRiskGuardStatus(token),
          getRiskGuardConfig(token),
          getRiskGuardEvents(token, 30),
        ]);
        if (!mountedRef.current || refreshId !== refreshIdRef.current) return;

        const nextConfig = normalizeConfig(configData);
        setStatus(normalizeStatus(statusData));
        setConfig(nextConfig);
        setBaselineSignature(configSignature(nextConfig));
        setPersistedConfig(nextConfig);
        setEvents(normalizeEvents(eventData));
        setError("");
      } catch (err) {
        if (mountedRef.current && refreshId === refreshIdRef.current) {
          setError(cleanUiError(err, t.loadError, t.methodError));
        }
      } finally {
        if (!silent && mountedRef.current && refreshId === refreshIdRef.current) setLoading(false);
      }
    },
    [agentToken, t.loadError, t.methodError]
  );

  useEffect(() => {
    if (!agentToken) {
      setLoading(false);
      setStatus(null);
      setEvents([]);
      return undefined;
    }

    try {
      localStorage.setItem(STORAGE_AGENT, agentToken);
    } catch {}

    loadAll(false);
    const timer = window.setInterval(() => refreshLive(true), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [agentToken, loadAll, refreshLive]);

  const online = Boolean(status?.online);
  const serverProtectionEnabled = Boolean(persistedConfig.enabled);
  const protectionEnabled = Boolean(
    status?.effective_enabled ??
      (serverProtectionEnabled && status?.agent_enabled !== false)
  );
  const hasTelemetry = Boolean(
    status?.last_sync ||
      status?.account_login ||
      status?.server ||
      status?.balance ||
      status?.equity
  );

  const blockedRule = String(status?.blocked_rule || "").trim().toLowerCase();
  const emergencyStopActive = Boolean(
    persistedConfig.block_new_orders || blockedRule === "emergency_stop"
  );
  const cooldownActive = Boolean(status?.blocked && blockedRule === "cooldown");
  const automaticBlocked = Boolean(
    status?.blocked && !emergencyStopActive && !cooldownActive
  );
  const blocked = Boolean(emergencyStopActive || cooldownActive || automaticBlocked);

  const riskScoreData = useMemo(
    () =>
      calculateRiskScore(status, persistedConfig, {
        enabled: protectionEnabled,
        hasTelemetry,
      }),
    [status, persistedConfig, protectionEnabled, hasTelemetry]
  );

  const riskScore = riskScoreData.score;
  const riskScoreAvailable = riskScoreData.available;
  const limitReached = riskScoreData.highestRatio >= 1;

  const riskState = limitReached
    ? { label: t.riskCritical, tone: "red" }
    : riskScore >= 60
      ? { label: t.riskWatch, tone: "amber" }
      : { label: t.riskControlled, tone: "green" };

  const blockLabel = emergencyStopActive
    ? t.manualStopActive
    : cooldownActive
      ? t.safetyPause
      : automaticBlocked
        ? t.automaticBlock
        : "";

  const blockReason = status?.blocked_rule
    ? ruleLabel(status.blocked_rule)
    : status?.blocked_reason || blockLabel;

  const protectionLabel = !protectionEnabled
    ? t.disabled
    : blocked
      ? blockLabel || t.blocked
      : online
        ? t.protected
        : t.configured;

  const marginUsed = Math.max(0, toNumber(status?.margin_used, 0));
  const accountEquity = Math.max(0, toNumber(status?.equity, 0));
  const freeMargin = Math.max(0, accountEquity - marginUsed);
  const marginLevel = Math.max(0, toNumber(status?.margin_level_pct, 0));
  const hasUsedMargin = marginUsed > 0.000001;

  const update = useCallback((key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }, []);

  const applyPreset = useCallback((name) => {
    const preset = PRESETS[name];
    if (!preset) return;
    setConfig((prev) => ({ ...prev, ...preset }));
    setSaved(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!agentToken || saving || !dirty) return;
    if (!isConfigValid(config)) {
      setError(t.invalidConfig);
      return;
    }

    setSaving(true);
    try {
      const payload = sanitizeConfig(config);
      const response = await saveRiskGuardConfig(agentToken, payload);
      if (!mountedRef.current) return;

      const nextConfig = response?.config ? normalizeConfig(response) : payload;
      setConfig(nextConfig);
      if (response?.status) setStatus(normalizeStatus(response));
      setBaselineSignature(configSignature(nextConfig));
      setPersistedConfig(nextConfig);
      setSaved(true);
      setError("");
      window.setTimeout(() => mountedRef.current && setSaved(false), 2000);
      await refreshLive(true);
    } catch (err) {
      if (mountedRef.current) setError(cleanUiError(err, t.saveError, t.methodError));
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [agentToken, config, dirty, refreshLive, saving, t.invalidConfig, t.methodError, t.saveError]);

  const handleManualRefresh = useCallback(async () => {
    if (dirty) await refreshLive(false);
    else await loadAll(false);
  }, [dirty, loadAll, refreshLive]);

  const handleEmergency = useCallback(async () => {
    if (!agentToken || actionLoading) return;
    if (!window.confirm(t.confirmStop)) return;
    setActionLoading(true);
    try {
      const response = await emergencyStopRiskGuard(agentToken);
      if (!mountedRef.current) return;
      const nextSaved = response?.config ? normalizeConfig(response) : { ...persistedConfig, block_new_orders: true };
      setPersistedConfig(nextSaved);
      setBaselineSignature(configSignature(nextSaved));
      setConfig((prev) => ({ ...prev, block_new_orders: nextSaved.block_new_orders }));
      if (response?.status) setStatus(normalizeStatus(response));
      setError("");
      await refreshLive(true);
    } catch (err) {
      if (mountedRef.current) setError(cleanUiError(err, t.stopError, t.methodError));
    } finally {
      if (mountedRef.current) setActionLoading(false);
    }
  }, [actionLoading, agentToken, persistedConfig, refreshLive, t.confirmStop, t.methodError, t.stopError]);

  const handleResume = useCallback(async () => {
    if (!agentToken || actionLoading) return;
    setActionLoading(true);
    try {
      const response = await resumeRiskGuard(agentToken);
      if (!mountedRef.current) return;
      const nextSaved = response?.config ? normalizeConfig(response) : { ...persistedConfig, block_new_orders: false };
      setPersistedConfig(nextSaved);
      setBaselineSignature(configSignature(nextSaved));
      setConfig((prev) => ({ ...prev, block_new_orders: nextSaved.block_new_orders }));
      if (response?.status) setStatus(normalizeStatus(response));
      setError("");
      await refreshLive(true);
    } catch (err) {
      if (mountedRef.current) setError(cleanUiError(err, t.resumeError, t.methodError));
    } finally {
      if (mountedRef.current) setActionLoading(false);
    }
  }, [actionLoading, agentToken, persistedConfig, refreshLive, t.methodError, t.resumeError]);

  const currency = status?.currency || "USD";
  const activePreset = useMemo(() => {
    const found = Object.entries(PRESETS).find(([, preset]) =>
      Object.entries(preset).every(([key, value]) => Number.isFinite(Number(value))
        ? Number(config[key]) === Number(value)
        : config[key] === value)
    );
    return found?.[0] || "";
  }, [config]);
  const restore = useCallback(() => {
    if (saving || actionLoading) return;
    setConfig(persistedConfig);
    setSaved(false);
    setError("");
  }, [persistedConfig, saving, actionLoading]);

  return (
    <main className="min-h-full overflow-x-clip bg-[#f6f7fb] px-3 pb-12 pt-4 text-[#071632] dark:bg-[#100d22] dark:text-slate-100 sm:px-5 sm:pt-5 lg:px-6 xl:px-7">
      {showAgentUpdateNotice ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-[3px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="risk-guard-agent-update-title"
        >
          <div className="relative w-full max-w-[520px] overflow-hidden rounded-[24px] border border-white/70 bg-white p-5 shadow-[0_30px_90px_rgba(15,23,42,0.28)] dark:border-white/10 dark:bg-[#19162f] sm:p-6">
            <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-violet-200/50 blur-3xl dark:bg-violet-500/15" />

            <button
              type="button"
              onClick={dismissAgentUpdateNotice}
              aria-label={t.agentUpdateLater}
              className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
            >
              <X size={18} />
            </button>

            <div className="relative">
              <div className="flex items-start gap-3.5">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-violet-600 text-white shadow-[0_8px_22px_rgba(124,58,237,0.22)]">
                  <Download size={21} strokeWidth={1.8} />
                </div>
                <div className="min-w-0 pr-8">
                  <span className="inline-flex rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.11em] text-violet-700 dark:bg-violet-400/10 dark:text-violet-300">
                    {t.agentUpdateBadge}
                  </span>
                  <h2
                    id="risk-guard-agent-update-title"
                    className="mt-2 text-[20px] font-semibold leading-[1.25] tracking-[-0.025em] text-[#0f1e37] dark:text-white"
                  >
                    {t.agentUpdateTitle}
                  </h2>
                </div>
              </div>

              <p className="mt-4 text-[13px] leading-6 text-slate-600 dark:text-slate-300">
                {t.agentUpdateText}
              </p>

              <div className="mt-5 rounded-[15px] border border-violet-100 bg-violet-50/60 p-3.5 dark:border-violet-400/15 dark:bg-violet-400/[0.06]">
                <div className="flex items-start gap-2.5">
                  <ShieldCheck size={16} className="mt-0.5 shrink-0 text-violet-600 dark:text-violet-300" />
                  <p className="text-[11px] leading-5 text-slate-600 dark:text-slate-300">
                    {lang === "fr"
                      ? "La mise à jour permet à l’Agent de synchroniser les règles, surveiller les limites et bloquer réellement les nouvelles entrées lorsque nécessaire."
                      : "The update lets the Agent sync rules, monitor limits and actually block new entries when required."}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={dismissAgentUpdateNotice}
                  className="inline-flex h-11 items-center justify-center rounded-xl border border-[#dce2ec] bg-white px-4 text-[12px] font-medium text-slate-600 transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
                >
                  {t.agentUpdateLater}
                </button>
                <button
                  type="button"
                  onClick={goToAgentPage}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 text-[12px] font-semibold text-white shadow-[0_6px_18px_rgba(124,58,237,0.20)] transition hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-violet-500/25"
                >
                  <Download size={15} />
                  {t.agentUpdateAction}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mx-auto w-full max-w-[1600px]">
        <header className="relative min-h-[118px] overflow-hidden rounded-[22px] border border-[#e2e7f0] bg-gradient-to-r from-white via-white to-[#f2efff] px-4 py-4 shadow-[0_6px_22px_rgba(15,23,42,0.04)] dark:border-white/10 dark:from-[#1b1734] dark:via-[#19162f] dark:to-[#242044] sm:px-5 sm:py-5 lg:px-6">
          <div aria-hidden="true" className="pointer-events-none absolute -right-10 -top-16 h-52 w-52 rounded-full bg-violet-200/25 blur-3xl dark:bg-violet-500/10" />
          <div className="relative flex min-h-[76px] flex-wrap items-center justify-between gap-4 lg:gap-6">
            <div className="flex min-w-0 flex-1 items-start gap-3.5 sm:gap-4">
              <div className="grid h-[48px] w-[48px] shrink-0 place-items-center rounded-[15px] bg-violet-600 text-white shadow-[0_7px_18px_rgba(109,40,217,0.20)] sm:h-[52px] sm:w-[52px]">
                <ShieldCheck size={24} strokeWidth={1.8} />
              </div>
              <div className="min-w-0 pt-0 sm:pt-0.5">
                <p className="mb-1 text-[9.5px] font-semibold uppercase tracking-[0.16em] text-violet-600 dark:text-violet-300 sm:text-[10px]">{t.eyebrow}</p>
                <h1 className="text-[26px] font-semibold leading-[1.08] tracking-[-0.035em] text-[#0b1b35] dark:text-white sm:text-[30px] lg:text-[32px]">{t.title}</h1>
                <p className="mt-1.5 max-w-[860px] text-[12px] leading-[1.55] text-slate-600 dark:text-slate-300 sm:text-[13px] lg:text-[13.5px]">{t.subtitle}</p>
              </div>
            </div>
            {agentToken && !loadingAgents && (
              <div className={cx(
                "inline-flex h-9 shrink-0 items-center gap-2 rounded-full border px-3.5 text-[11.5px] font-semibold shadow-sm",
                online ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300" : "border-slate-200 bg-white/90 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
              )}>
                <span className={cx("h-2 w-2 rounded-full", online ? "bg-emerald-500" : "bg-slate-400")} />
                {online ? t.online : t.offline}
              </div>
            )}
          </div>
        </header>

        {agentToken ? (
          <div className="sticky top-[76px] z-30 mt-3.5 rounded-[18px] border border-[#dde4ee] bg-white/96 px-3.5 py-3.5 shadow-[0_8px_24px_rgba(15,23,42,0.055)] backdrop-blur-xl dark:border-white/10 dark:bg-[#19162f]/95 sm:px-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-2.5">
                <span
                  className={cx(
                    "inline-flex min-h-8 items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-[12px] font-medium",
                    online
                      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300"
                      : "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300"
                  )}
                >
                  {online ? <Wifi size={13} /> : <WifiOff size={13} />}
                  {online ? t.online : t.offline}
                </span>
                <span
                  className={cx(
                    "inline-flex min-h-8 items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-[12px] font-medium",
                    blocked
                      ? "bg-rose-50 text-rose-700 dark:bg-rose-400/10 dark:text-rose-300"
                      : protectionEnabled
                        ? "bg-violet-50 text-violet-700 dark:bg-violet-400/10 dark:text-violet-300"
                        : "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300"
                  )}
                >
                  {blocked ? <ShieldAlert size={13} /> : <ShieldCheck size={13} />}
                  {protectionLabel}
                </span>
                <span className={cx("text-[12px] font-normal", dirty ? "text-amber-600" : "text-slate-400")}>
                  {dirty ? t.unsaved : t.upToDate}
                </span>
              </div>

              <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
                {dirty && (
                  <button type="button" disabled={saving || actionLoading} onClick={restore} className="hidden rounded-xl px-3 py-2 text-[12px] font-normal text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-white/10 md:inline-flex">
                    {t.restore}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleManualRefresh}
                  disabled={loading || loadingAgents || saving}
                  title={t.refresh}
                  aria-label={t.refresh}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-[#d9e0ea] bg-white text-[13px] font-medium text-[#17243b] transition hover:bg-slate-50 disabled:opacity-50 dark:border-white/10 dark:bg-[#211d3a] dark:text-white dark:hover:bg-white/10 sm:w-auto sm:px-4"
                >
                  <RefreshCcw size={14} className={loading ? "animate-spin" : ""} />
                  <span className="hidden sm:inline">{t.refresh}</span>
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!dirty || saving || actionLoading || !isConfigValid(config)}
                  className="inline-flex h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 text-[13px] font-semibold text-white shadow-[0_6px_16px_rgba(124,58,237,0.18)] transition hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-violet-500/25 disabled:cursor-not-allowed disabled:opacity-45 sm:flex-none"
                >
                  {saving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : saved ? (
                    <CheckCircle2 size={14} />
                  ) : (
                    <Save size={14} />
                  )}
                  {saved ? t.saved : t.save}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {agentToken && dirty && !isConfigValid(config) && (
          <div role="alert" className="mt-3 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-[12px] font-normal text-amber-800 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-200">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{t.invalidConfig}</span>
          </div>
        )}
        {error ? (
          <div role="alert" className="mt-3 flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-3 text-[12px] font-medium text-rose-700 dark:border-rose-400/20 dark:bg-rose-400/10 dark:text-rose-200">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        {loadingAgents || (loading && agentToken && !status) ? (
          <Card className="mt-5 flex min-h-[220px] flex-col items-center justify-center gap-3 p-6 text-center" >
            <Loader2 className="animate-spin text-violet-600" size={25} />
            <p className="text-[13px] font-normal text-slate-600 dark:text-slate-300">{t.loadingData}</p>
          </Card>
        ) : !agentToken ? (
          <Card className="mt-5 px-5 py-12 text-center sm:px-6">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-400/10 dark:text-violet-300">
              <ShieldAlert size={24} />
            </div>
            <h2 className="mt-4 text-[18px] font-medium text-[#071632] dark:text-white">{t.noAgentTitle}</h2>
            <p className="mx-auto mt-2 max-w-xl text-[13px] leading-5 text-slate-500 dark:text-slate-400">{t.noAgentText}</p>
          </Card>
        ) : (
          <>
            <section aria-label={t.guardStatus} className="mt-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <SummaryItem
                  icon={blocked ? ShieldAlert : ShieldCheck}
                  tone={blocked ? "red" : protectionEnabled ? "violet" : "slate"}
                  label={t.guardStatus}
                  value={protectionLabel}
                  detail={online ? t.online : t.offline}
                />
                <SummaryItem
                  icon={CircleDollarSign}
                  tone="violet"
                  label={t.mt5Account}
                  value={status?.account_login || "—"}
                  detail={status?.server || (hasTelemetry ? money(status?.balance, currency) : t.waitingData)}
                />
                <SummaryItem
                  icon={BarChart3}
                  tone={!hasTelemetry ? "slate" : riskState.tone}
                  label={t.currentRisk}
                  value={riskScoreAvailable ? `${riskScore}%` : "—"}
                  detail={
                    !protectionEnabled
                      ? t.riskDisabledHint
                      : hasTelemetry
                        ? riskState.label
                        : t.waitingData
                  }
                />
                <SummaryItem
                  icon={online ? Wifi : WifiOff}
                  tone={online ? "blue" : "slate"}
                  label={t.lastSync}
                  value={status?.last_sync ? dateTime(status.last_sync) : t.neverSynced}
                  detail={`${online ? t.online : t.waitingConnection} · ${t.localTime}: ${userTimeZone}`}
                />
              </div>
            </section>

            <section className="mt-4 grid min-w-0 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
              <Card className="overflow-hidden">
                <div className="flex flex-col gap-3 border-b border-[#e9edf3] px-4 py-5 dark:border-white/10 sm:flex-row sm:items-start sm:justify-between sm:px-5">
                  <div className="flex items-start gap-3">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-400/10 dark:text-violet-300">
                      <SlidersHorizontal size={17} />
                    </div>
                    <div>
                      <h2 className="text-[20px] font-semibold tracking-[-0.025em] text-[#10203a] dark:text-white">{t.configuration}</h2>
                      <p className="mt-1 text-[13px] leading-[1.5] text-slate-500 dark:text-slate-400">{t.configurationDesc}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                    <RefreshCcw size={12} />
                    <span>{t.syncHint}</span>
                  </div>
                </div>

                <div className="p-4 sm:p-5">
                  <div className="rounded-[16px] border border-violet-100 bg-violet-50/60 p-3.5 dark:border-violet-400/15 dark:bg-violet-400/[0.06] sm:p-4">
                    <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Sparkles size={14} className="text-violet-600 dark:text-violet-300" />
                          <p className="text-[12px] font-medium text-[#071632] dark:text-white">{t.quickProfiles}</p>
                        </div>
                        <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{t.quickProfilesDesc}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {[
                          ["prudent", t.prudent],
                          ["balanced", t.balanced],
                          ["flexible", t.flexible],
                        ].map(([key, label]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => applyPreset(key)}
                            aria-pressed={activePreset === key}
                            className={cx(
                              "rounded-[10px] border px-3.5 py-2 text-[12px] font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/30",
                              activePreset === key
                                ? "border-violet-600 bg-violet-600 text-white dark:border-violet-500 dark:bg-violet-500"
                                : "border-violet-200 bg-white text-violet-700 hover:border-violet-300 hover:bg-violet-50 dark:border-violet-400/20 dark:bg-white/5 dark:text-violet-200 dark:hover:bg-white/10"
                            )}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <SectionHeader title={t.mainLimits} />
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <CompactField
                        label={t.dailyLossMax}
                        value={config.max_daily_loss_pct}
                        onChange={(value) => update("max_daily_loss_pct", value)}
                        suffix="%"
                        min={0.1}
                        max={100}
                        step={0.1}
                        help={t.dailyLossHelp}
                      />
                      <CompactField
                        label={t.drawdownMax}
                        value={config.max_drawdown_pct}
                        onChange={(value) => update("max_drawdown_pct", value)}
                        suffix="%"
                        min={0.1}
                        max={100}
                        step={0.1}
                        help={t.drawdownHelp}
                      />
                      <CompactField
                        label={t.riskPerTrade}
                        value={config.max_risk_per_trade_pct}
                        onChange={(value) => update("max_risk_per_trade_pct", value)}
                        suffix="%"
                        min={0.01}
                        max={100}
                        step={0.1}
                        help={t.riskPerTradeHelp}
                      />
                      <CompactField
                        label={t.simultaneousPositions}
                        value={config.max_open_positions}
                        onChange={(value) => update("max_open_positions", value)}
                        suffix={t.positionsUnit}
                        min={1}
                        max={100}
                        step={1}
                        help={t.simultaneousPositionsHelp}
                      />
                    </div>
                  </div>

                  <div className="mt-6 border-t border-[#edf0f4] pt-5 dark:border-white/10">
                    <SectionHeader title={t.accountSafety} />
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <CompactField
                        label={t.totalExposure}
                        value={config.max_total_exposure_pct}
                        onChange={(value) => update("max_total_exposure_pct", value)}
                        suffix="%"
                        min={0.1}
                        max={1000}
                        step={0.5}
                        help={t.totalExposureHelp}
                      />
                      <CompactField
                        label={t.minMargin}
                        value={config.min_margin_level_pct}
                        onChange={(value) => update("min_margin_level_pct", value)}
                        suffix="%"
                        min={0}
                        max={10000}
                        step={10}
                        help={t.minMarginHelp}
                      />
                      <CompactField
                        label={t.consecutiveLosses}
                        value={config.max_consecutive_losses}
                        onChange={(value) => update("max_consecutive_losses", value)}
                        suffix={t.tradesUnit}
                        min={1}
                        max={100}
                        step={1}
                        help={t.consecutiveLossesHelp}
                      />
                      <CompactField
                        label={t.cooldown}
                        value={config.cooldown_minutes}
                        onChange={(value) => update("cooldown_minutes", value)}
                        suffix={t.minutesUnit}
                        min={0}
                        max={1440}
                        step={5}
                        help={t.cooldownHelp}
                      />
                    </div>
                  </div>

                  <div className="mt-6 border-t border-[#edf0f4] pt-5 dark:border-white/10">
                    <SectionHeader title={t.automation} />
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <ToggleCard
                        checked={Boolean(config.enabled)}
                        onChange={(value) => update("enabled", value)}
                        label={t.protectionEnabled}
                        description={t.protectionEnabledDesc}
                      />
                      <ToggleCard
                        checked={Boolean(config.lock_on_violation)}
                        onChange={(value) => update("lock_on_violation", value)}
                        label={t.lockViolation}
                        description={t.lockViolationDesc}
                      />
                      <ToggleCard
                        checked={Boolean(config.require_stop_loss)}
                        onChange={(value) => update("require_stop_loss", value)}
                        label={t.requireStopLoss}
                        description={t.requireStopLossDesc}
                      />
                    </div>
                    <div className="mt-3 flex items-start gap-2 rounded-xl border border-sky-100 bg-sky-50/70 px-3 py-2.5 text-[11px] leading-4 text-sky-800 dark:border-sky-400/15 dark:bg-sky-400/[0.06] dark:text-sky-200">
                      <Info size={14} className="mt-0.5 shrink-0" />
                      <span>{t.allEntriesProtected}</span>
                    </div>
                  </div>
                </div>
              </Card>

              <aside className="min-w-0 space-y-4 xl:sticky xl:top-[144px] xl:self-start">
                <Card className="p-4 sm:p-5">
                  <div className="flex items-center gap-2.5">
                    <div className="grid h-9 w-9 place-items-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-400/10 dark:text-violet-300">
                      <Activity size={17} />
                    </div>
                    <div>
                      <h2 className="text-[17px] font-semibold text-[#071632] dark:text-white">{t.liveProtection}</h2>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">{t.liveProtectionDesc}</p>
                    </div>
                  </div>

                  <div className="mt-4 rounded-[16px] border border-[#edf0f6] bg-[#f7f8fc] p-4 dark:border-white/5 dark:bg-white/[0.035]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">{t.riskScore}</p>
                        <p className="mt-1 text-[32px] font-medium tracking-[-0.045em] text-[#14223b] dark:text-white">
                          {riskScoreAvailable ? `${riskScore}%` : "—"}
                        </p>
                      </div>
                      <div
                        className={cx(
                          "grid h-10 w-10 place-items-center rounded-xl",
                          !riskScoreAvailable
                            ? "bg-slate-100 text-slate-500 dark:bg-white/10"
                            : limitReached
                              ? "bg-rose-50 text-rose-600 dark:bg-rose-400/10 dark:text-rose-300"
                              : riskScore >= 60
                                ? "bg-amber-50 text-amber-600 dark:bg-amber-400/10 dark:text-amber-300"
                                : "bg-emerald-50 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-300"
                        )}
                      >
                        <Gauge size={19} />
                      </div>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                      <div
                        className={cx(
                          "h-full rounded-full transition-all",
                          limitReached ? "bg-rose-500" : riskScore >= 60 ? "bg-amber-500" : "bg-emerald-500"
                        )}
                        style={{ width: `${riskScoreAvailable ? riskScore : 0}%` }}
                      />
                    </div>
                    <p className="mt-2 text-[11px] font-normal text-slate-500 dark:text-slate-400">
                      {!protectionEnabled
                        ? t.disabled
                        : riskScoreAvailable
                          ? riskState.label
                          : t.waitingData}
                    </p>
                    <p className="mt-1 text-[10px] leading-4 text-slate-400 dark:text-slate-500">
                      {!protectionEnabled ? t.riskDisabledHint : t.riskEstimate}
                    </p>
                  </div>

                  {blocked ? (
                    <div className={cx(
                      "mt-3 rounded-[14px] border p-3.5",
                      emergencyStopActive
                        ? "border-rose-200 bg-rose-50/80 text-rose-800 dark:border-rose-400/20 dark:bg-rose-400/10 dark:text-rose-200"
                        : "border-amber-200 bg-amber-50/80 text-amber-900 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-100"
                    )}>
                      <div className="flex items-start gap-2.5">
                        <ShieldAlert size={16} className="mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold">{t.blockReasonTitle}</p>
                          <p className="mt-1 text-[11px] leading-4">{blockReason || protectionLabel}</p>
                          {status?.blocked_until ? (
                            <p className="mt-1 text-[10px] opacity-75">
                              {t.blockedUntil}: {dateTime(status.blocked_until)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <p className="mt-2 border-t border-current/10 pt-2 text-[10px] leading-4 opacity-75">
                        {t.blockIndependentScore}
                      </p>
                    </div>
                  ) : null}

                  <div className="mt-5 space-y-5">
                    <ProgressRow
                      label={t.dailyLoss}
                      valueText={hasTelemetry ? `${number(status?.daily_loss_pct)}% / ${number(persistedConfig.max_daily_loss_pct)}%` : "—"}
                      ratio={toNumber(status?.daily_loss_pct) / Math.max(toNumber(persistedConfig.max_daily_loss_pct), 0.0001)}
                      tone={
                        toNumber(status?.daily_loss_pct) >= toNumber(persistedConfig.max_daily_loss_pct)
                          ? "red"
                          : toNumber(status?.daily_loss_pct) >= toNumber(persistedConfig.max_daily_loss_pct) * 0.75
                            ? "amber"
                            : "violet"
                      }
                      detail={hasTelemetry ? `${t.dailyPnl}: ${money(status?.daily_pnl, currency)}` : t.waitingData}
                    />
                    <ProgressRow
                      label={t.drawdown}
                      valueText={hasTelemetry ? `${number(status?.drawdown_pct)}% / ${number(persistedConfig.max_drawdown_pct)}%` : "—"}
                      ratio={toNumber(status?.drawdown_pct) / Math.max(toNumber(persistedConfig.max_drawdown_pct), 0.0001)}
                      tone={
                        toNumber(status?.drawdown_pct) >= toNumber(persistedConfig.max_drawdown_pct)
                          ? "red"
                          : toNumber(status?.drawdown_pct) >= toNumber(persistedConfig.max_drawdown_pct) * 0.75
                            ? "amber"
                            : "violet"
                      }
                    />
                    <ProgressRow
                      label={t.exposure}
                      valueText={hasTelemetry ? `${number(status?.total_exposure_pct)}% / ${number(persistedConfig.max_total_exposure_pct)}%` : "—"}
                      ratio={toNumber(status?.total_exposure_pct) / Math.max(toNumber(persistedConfig.max_total_exposure_pct), 0.0001)}
                      tone="blue"
                    />
                    <ProgressRow
                      label={t.positions}
                      valueText={hasTelemetry ? `${status?.open_positions ?? 0} / ${persistedConfig.max_open_positions}` : "—"}
                      ratio={toNumber(status?.open_positions) / Math.max(toNumber(persistedConfig.max_open_positions), 1)}
                      tone="violet"
                    />
                    <ProgressRow
                      label={t.marginLevel}
                      valueText={
                        !hasTelemetry
                          ? "—"
                          : !hasUsedMargin
                            ? t.marginNotUsed
                            : `${number(marginLevel)}% / ${t.minLabel} ${number(persistedConfig.min_margin_level_pct)}%`
                      }
                      ratio={
                        !hasUsedMargin || marginLevel <= 0
                          ? 0
                          : toNumber(persistedConfig.min_margin_level_pct) / marginLevel
                      }
                      tone={
                        hasUsedMargin && marginLevel > 0 && marginLevel <= toNumber(persistedConfig.min_margin_level_pct)
                          ? "red"
                          : hasUsedMargin && marginLevel > 0 && marginLevel <= toNumber(persistedConfig.min_margin_level_pct) * 1.25
                            ? "amber"
                            : "green"
                      }
                    />
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-2.5">
                    {[
                      [t.balance, money(status?.balance, currency)],
                      [t.equity, money(status?.equity, currency)],
                      [t.freeMargin, money(freeMargin, currency)],
                      [t.marginUsed, money(marginUsed, currency)],
                      [t.marginLevel, hasUsedMargin && marginLevel > 0 ? `${number(marginLevel)}%` : t.marginNotUsed],
                      [t.consecutive, String(status?.consecutive_losses ?? 0)],
                    ].map(([label, value]) => (
                      <div key={label} className="min-w-0 rounded-[13px] border border-[#e6eaf0] bg-[#fcfcfe] px-3.5 py-3 dark:border-white/10 dark:bg-white/[0.025]">
                        <p className="text-[11px] font-medium text-slate-400">{label}</p>
                        <p className="mt-1 break-words text-[13px] font-medium leading-5 text-[#14223b] dark:text-white">{hasTelemetry ? value : "—"}</p>
                      </div>
                    ))}
                  </div>

                </Card>

                <Card className={cx(
                  "p-4 sm:p-5",
                  blocked
                    ? "border-emerald-200/80 dark:border-emerald-400/20"
                    : "border-rose-200/80 dark:border-rose-400/20"
                )}>
                  <div className="flex items-start gap-2.5">
                    <div className={cx(
                      "grid h-9 w-9 shrink-0 place-items-center rounded-xl",
                      blocked
                        ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-300"
                        : "bg-rose-50 text-rose-600 dark:bg-rose-400/10 dark:text-rose-300"
                    )}>
                      <LockKeyhole size={17} />
                    </div>
                    <div>
                      <h2 className="text-[16px] font-semibold text-[#071632] dark:text-white">{t.emergencyStop}</h2>
                      <p className="mt-0.5 text-[11px] leading-4 text-slate-500 dark:text-slate-400">{t.emergencyDesc}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={blocked ? handleResume : handleEmergency}
                    disabled={actionLoading}
                    className={cx(
                      "mt-4 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl px-3 text-[13px] font-medium text-white transition focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-rose-400/20 disabled:opacity-60",
                      blocked ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
                    )}
                  >
                    {actionLoading ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : blocked ? (
                      <Check size={14} />
                    ) : (
                      <Ban size={14} />
                    )}
                    {blocked ? t.allowResume : t.blockOrders}
                  </button>
                </Card>

                <div className="flex gap-3 rounded-[16px] border border-amber-200/70 bg-amber-50/70 p-4 text-amber-900 dark:border-amber-400/15 dark:bg-amber-400/[0.06] dark:text-amber-100">
                  <Info size={15} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-300" />
                  <div>
                    <p className="text-[11px] font-medium">{t.goodToKnow}</p>
                    <p className="mt-0.5 text-[11px] leading-4 text-amber-800/80 dark:text-amber-100/70">{t.goodToKnowText}</p>
                  </div>
                </div>
              </aside>
            </section>

            <Card className="mt-4 overflow-hidden">
              <div className="flex items-center justify-between gap-3 border-b border-[#e6eaf0] px-4 py-4 dark:border-white/10 sm:px-5">
                <div className="flex items-center gap-2.5">
                  <div className="grid h-8 w-8 place-items-center rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-400/10 dark:text-violet-300">
                    <Zap size={15} />
                  </div>
                  <div>
                    <h2 className="text-[16px] font-semibold text-[#071632] dark:text-white">{t.events}</h2>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">{t.eventsDesc}</p>
                  </div>
                </div>
                <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-lg bg-slate-100 px-2 text-[11px] font-medium text-slate-600 dark:bg-white/10 dark:text-slate-300">
                  {events.length}
                </span>
              </div>

              <div className="divide-y divide-[#edf0f5] dark:divide-white/5">
                {events.length ? (
                  events.slice(0, 12).map((event) => (
                    <div key={event.id} className="flex items-start gap-3 px-4 py-3.5 transition hover:bg-slate-50/70 dark:hover:bg-white/[0.025] sm:px-5">
                      <span
                        className={cx(
                          "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                          event.severity === "critical"
                            ? "bg-rose-500"
                            : event.severity === "warning"
                              ? "bg-amber-500"
                              : "bg-violet-500"
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-[12px] font-medium text-[#071632] dark:text-slate-100">{ruleLabel(event.rule_key)}</p>
                          <time className="shrink-0 text-[11px] font-medium text-slate-400">
                            {event.created_at ? dateTime(event.created_at) : ""}
                          </time>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-5 py-10 text-center text-[12px] text-slate-500 dark:text-slate-400">{t.noEvents}</div>
                )}
              </div>
            </Card>
          </>
        )}
      </div>
    </main>
  );
}
